import type { components } from '@feedfm/clips-services-api';
import { Pause, PlayArrow } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useStudioAuthenticatedContext } from '../../context/StudioContext';
import { CollectionArtwork } from '../CollectionArtwork';
import * as styles from './CollectionThumbnail.styles';

export interface CollectionThumbnailProps {
  collection:
    | components['schemas']['CollectionSummary']
    | components['schemas']['Collection'];
  /** When set, uses API collection artwork (e.g. collection detail); otherwise derived client-side. */
  artworkPreSignedUrls: components['schemas']['ArtworkPreSignedUrls'];
  /** Server-generated composite thumbnail URL. When present, the inner CollectionArtwork
   *  renders this instead of the 4-cell grid. */
  thumbnailUrl?: string | null;
  /** Size variant for the thumbnail */
  size?: 'medium' | 'xlarge' | 'large';
  /** Custom click handler, defaults to playing the collection */
  onClick?: () => void;
  /** Whether to show play button overlay */
  showPlayButton?: boolean;
  /** External hover state to force show overlay */
  externalHovered?: boolean;
  /** When true, overlay shows a spinner instead of play/pause */
  isLoading?: boolean;
}

export const CollectionThumbnail = ({
  collection,
  artworkPreSignedUrls,
  thumbnailUrl,
  size = 'medium',
  onClick,
  showPlayButton = true,
  externalHovered = false,
  isLoading = false,
}: CollectionThumbnailProps) => {
  const [localHovered, setLocalHovered] = useState(false);
  const { playerState, collectionSort } = useStudioAuthenticatedContext();

  // Check if this collection is currently playing
  const isPlaying =
    playerState.isCollectionPlaying(collection.collectionId) &&
    playerState.playIntent;

  // Handle click - use custom handler or default to playing collection
  const handleClick = () => {
    if (isLoading) {
      return;
    }
    if (onClick) {
      onClick();
    } else if ('clips' in collection) {
      playerState.clickCollection(collection, collectionSort);
    }
  };

  const hovered = localHovered || externalHovered;
  const isClickable = !isLoading && Boolean(onClick || 'clips' in collection);

  return (
    <Box
      sx={{
        ...styles.container(size),
        cursor: isClickable ? 'pointer' : 'default',
      }}
      onMouseEnter={() => setLocalHovered(true)}
      onMouseLeave={() => setLocalHovered(false)}
      onClick={handleClick}
    >
      <CollectionArtwork
        artworkPreSignedUrls={artworkPreSignedUrls}
        thumbnailUrl={thumbnailUrl}
      />

      {showPlayButton && (
        <Box
          sx={{
            ...styles.playButtonOverlay,
            opacity: isLoading || hovered ? 1 : 0,
            pointerEvents: 'none',
            cursor: isClickable ? 'pointer' : 'default',
          }}
        >
          {isLoading ? (
            <CircularProgress size={36} sx={{ color: 'common.white' }} />
          ) : isPlaying ? (
            <Pause sx={styles.playIcon} />
          ) : (
            <PlayArrow sx={styles.playIcon} />
          )}
        </Box>
      )}
    </Box>
  );
};
